// Shared hyper-local copy for blog / symptom / brand / commercial content templates.
export const LOCAL_HUBS = ["Decatur", "Tucker", "Stone Mountain", "Lithonia", "Ellenwood", "Stockbridge"];
export const DECATUR_ZIPS = ["30030", "30032", "30033", "30034", "30035"];

export const NAP = {
  name: "Decatur Solution Works LLC",
  street: "2318 Newgate Drive",
  cityStateZip: "Decatur, GA 30035",
  phone: "(678) 525-0474",
  phoneRaw: "+16785250474",
  lat: 33.7215,
  lng: -84.2257,
};

export const LOCAL_NOTICE = {
  residential:
    "Decatur Solution Works provides residential appliance repair strictly in Decatur, DeKalb County, and surrounding Metro Atlanta communities. We do not dispatch outside our Georgia service footprint.",
  commercial:
    "Decatur Solution Works provides EPA 608 certified commercial refrigeration repair strictly in Decatur, DeKalb County, and surrounding Metro Atlanta communities. We do not dispatch outside our Georgia service footprint.",
};

export const SERVICE_PHRASE = {
  residential: "Servicing homeowners throughout Decatur, Tucker, Stone Mountain, Lithonia, Ellenwood, and Stockbridge.",
  commercial: "Servicing restaurants, grocery stores and facilities throughout Decatur, Tucker, Stone Mountain, Lithonia, Ellenwood, and Stockbridge.",
};

export const RES_FEE = "$89 diagnostic fee, credited toward your repair";
export const COM_FEE = "$250 commercial diagnostic fee (flat-rate, covers the full on-site equipment inspection)";

export const PRICING_SENTENCE = {
  residential: `${RES_FEE}, backed by an EPA 608 Certified technician and a 1-year parts & labor warranty (one year on OEM parts)`,
  commercial: `${COM_FEE}, backed by an EPA 608 Certified technician and a 1-year parts & labor warranty (one year on OEM parts)`,
};

// City landing page slugs for the six local hubs (used for contextual internal links in guides).
export const HUB_CITY_LINKS = {
  Decatur: "/appliance-repair-decatur",
  Tucker: "/appliance-repair-tucker",
  "Stone Mountain": "/appliance-repair-stone-mountain",
  Lithonia: "/appliance-repair-lithonia",
  Ellenwood: "/appliance-repair-ellenwood",
  Stockbridge: "/appliance-repair-stockbridge",
};

export const COVERAGE_BULLETS = [
  `Decatur, GA hub — ZIPs ${DECATUR_ZIPS.join(", ")}`,
  "DeKalb County: Tucker, Stone Mountain, Lithonia, Clarkston, Avondale Estates",
  "Metro Atlanta: Atlanta, Ellenwood, Stockbridge, Conyers",
  "Georgia service footprint only — no out-of-state dispatch",
];

const COMMERCIAL_RE = /commercial|walk-in|reach-in|restaurant|ice machine/i;
export const isCommercialPost = (post) =>
  COMMERCIAL_RE.test(`${post.slug || ""} ${post.title || ""} ${post.topic || ""}`);

const LOCAL_TERM_RE = /decatur|atlanta|dekalb|georgia|\bGA\b/i;
export const localizeTitle = (title = "") => {
  const t = title.trim();
  if (!t || LOCAL_TERM_RE.test(t)) return t;
  return /[?!]$/.test(t) ? `${t} A Decatur, GA Guide` : `${t} in Decatur, GA`;
};

// Render-time normalization: legacy "waived" wording → credited policy; any 30/60/90-day warranty → 1-year.
const WAIVED_RE = /\$89 diagnostic fee \(waived if parts are stocked and repair is approved on-site\)/g;
const WARRANTY_RE = /\b(?:30|60|90)[- ]day\s+(?:parts\s*(?:&|and)\s*)?(?:labor\s+)?warranty(?:\s*\([^)]*\))?/gi;

export const standardizePricing = (html = "") => html.replace(WAIVED_RE, RES_FEE).replace(WARRANTY_RE, "1-year parts & labor warranty (one year on OEM parts)");

// Inserts the local service paragraph as paragraph #2 of the article body.
export const injectLocalIntro = (html = "", variant = "residential") => {
  const para = `<p class="my-4 text-[16px] leading-[1.75] text-slate-700" data-testid="local-service-paragraph"><strong class="font-bold text-slate-900">${SERVICE_PHRASE[variant]}</strong> Every visit starts with a ${PRICING_SENTENCE[variant]}.</p>`;
  const idx = html.indexOf("</p>");
  return idx === -1 ? para + html : html.slice(0, idx + 4) + "\n" + para + html.slice(idx + 4);
};

// Links the first plain-text mention of each hub city to its city landing page (skips headings + existing anchors).
const CITY_LINK_CLASS = "text-orange-700 font-semibold underline decoration-orange-200 hover:decoration-orange-500 underline-offset-2";
export const linkHubCities = (html = "") => {
  let out = html;
  for (const [city, href] of Object.entries(HUB_CITY_LINKS)) {
    const re = new RegExp(`(<p[^>]*>(?:(?!</p>).)*?)(?<![\\w-])(${city})(?![\\w-])(?!\\s+Solution)(?![^<]*</a>)`, "s");
    if (out.includes(`href="${href}"`)) continue;
    out = out.replace(re, `$1<a href="${href}" class="${CITY_LINK_CLASS}" data-city-link="${href.slice(1)}">$2</a>`);
  }
  return out;
};

// Guide slug -> appliance page with a "What repairs cost" section (#repair-costs).
export const GUIDE_COST_PAGE = {
  "refrigerator-not-cooling-but-lights-on-diagnostic-guide": "/refrigerator-repair",
  "samsung-refrigerator-leaking-water-diagnostic-guide": "/refrigerator-repair",
  "ice-maker-not-working-diagnostic-guide": "/ice-maker-repair",
  "dryer-not-heating-samsung-whirlpool-diagnostic-guide": "/dryer-repair",
  "washer-wont-drain-or-spin-diagnostic-guide": "/washing-machine-repair",
  "oven-not-heating-or-wrong-temperature-diagnostic-guide": "/oven-repair",
  "gas-stove-burner-wont-light-diagnostic-guide": "/stove-repair",
  "dishwasher-not-draining-or-cleaning-diagnostic-guide": "/dishwasher-repair",
  "commercial-walk-in-cooler-compressor-failure-diagnostic-guide": "/commercial-refrigeration-rack-repair",
  "commercial-reach-in-freezer-not-cooling-diagnostic-guide": "/commercial-refrigeration-rack-repair",
};
const COST_LABEL = { "/commercial-refrigeration-rack-repair": "commercial refrigeration", "/refrigerator-repair": "refrigerator", "/ice-maker-repair": "ice maker", "/dryer-repair": "dryer", "/washing-machine-repair": "washing machine", "/oven-repair": "oven", "/stove-repair": "stove", "/dishwasher-repair": "dishwasher" };
// Appends a cost-section link after the first residential pricing mention in a guide body.
export const linkRepairCosts = (html = "", slug = "") => {
  const page = GUIDE_COST_PAGE[slug];
  if (!page) return html;
  const re = page.startsWith("/commercial") ? /(\$250 flat-rate diagnostic[^<]*)/ : /(\$89 diagnostic, credited toward your repair[^<]*)/;
  if (!re.test(html)) return html;
  return html.replace(re, `$1 <a href="${page}#repair-costs" class="${CITY_LINK_CLASS}" data-testid="guide-repair-cost-link">See what ${COST_LABEL[page]} repairs cost in Decatur →</a>`);
};

// Blog bodies: any "/contact" link whose text starts a booking goes straight to the booking form.
export const pointBookingLinks = (html = "") =>
  html.replace(/<a([^>]*?)href="\/contact"([^>]*)>([^<]*(?:book|schedule|request service)[^<]*)<\/a>/gi, '<a$1href="/contact#book"$2>$3</a>');
